/**
 * Auth Background Service — module-level singleton.
 *
 * Hoists the auth-related background work (activity-tracking DOM listeners
 * + the token-refresh setInterval) OUT of React's render tree.  Before
 * this refactor, both lived inside ``AuthProvider`` and re-registered on
 * every AuthProvider render — which, in production, fired thousands of
 * times per minute because of unrelated state updates.  Moving them
 * here gives:
 *
 *   1. ONE pair of listener registrations per page load (vs N per render)
 *   2. ONE setInterval per page load (vs N per render)
 *   3. Activity updates happen via module-scope variable (no React state
 *      changes, no re-renders, no cascade)
 *   4. AuthProvider becomes a thin shell that just calls start()/stop()
 *      when user logs in / out
 *
 * Lifecycle:
 *   - start({ onTokenRefreshFail })  — call when user becomes logged in
 *   - stop()                          — call when user logs out
 *   - getLastActivity()               — read the last-activity timestamp
 *
 * The service is idempotent: calling start() twice does nothing the 2nd
 * time; calling stop() before start() is a no-op.
 *
 * (Feb 2026 — Workstream A: "hoist background security timers out of the
 * render tree to drop background CPU idle usage to near-zero.")
 */

import { getAuthToken } from './authHelpers';
import { refreshAuthToken } from '../hooks/useTokenRefresh';

// ---- Constants (must match AuthContext.jsx) -------------------------------
const TOKEN_REFRESH_INTERVAL_MS = 15 * 60 * 1000;        // 15 min
const ACTIVITY_TIMEOUT_MS       = 30 * 60 * 1000;        // 30 min
const ACTIVITY_THROTTLE_MS      = 5 * 1000;              // 5 s
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'];

// ---- Module-scope state — lives OUTSIDE React tree ------------------------
let _started = false;
let _lastActivity = Date.now();
let _lastActivityThrottleMark = 0;
let _refreshIntervalId = null;
let _onTokenRefreshFail = null;

// ---- Activity listener (single instance, attached once) -------------------
function _onActivity() {
  const now = Date.now();
  if (now - _lastActivityThrottleMark <= ACTIVITY_THROTTLE_MS) return;
  _lastActivityThrottleMark = now;
  _lastActivity = now;
}

// ---- Token-refresh tick — fires every TOKEN_REFRESH_INTERVAL_MS ----------
async function _tokenRefreshTick() {
  // Bail if there's no token (logged out / session cleared).
  const token = getAuthToken();
  if (!token) return;

  // Bail if user has been inactive longer than ACTIVITY_TIMEOUT_MS.
  // (We still let the cookie expire naturally; we just don't push
  // forward the session window when the user is away.)
  if (Date.now() - _lastActivity >= ACTIVITY_TIMEOUT_MS) return;

  // Active user — push the session window forward.
  // (Jun 2026 session-stability fix): refreshAuthToken resolves null ONLY
  // when the session is definitively invalid; it THROWS on transient
  // network/5xx failures.  Only the definitive case may log the user out —
  // a transient blip must never boot an active user (we just retry on the
  // next 15-min tick; the token is valid for 24h).
  try {
    const newToken = await refreshAuthToken();
    if (!newToken && typeof _onTokenRefreshFail === 'function') {
      _onTokenRefreshFail();
    }
  } catch (err) {
    // Transient failure — keep the session, retry next tick.
  }
}

// ---- Public API -----------------------------------------------------------

/**
 * Start the background service.  Idempotent — calling twice is a no-op.
 *
 * @param {object} options
 * @param {Function} options.onTokenRefreshFail — called when the periodic
 *   token-refresh fails.  Typical implementation: logout() + redirect.
 */
export function startAuthBackgroundService({ onTokenRefreshFail } = {}) {
  if (_started) return;
  _started = true;
  _onTokenRefreshFail = onTokenRefreshFail || null;
  _lastActivity = Date.now();
  _lastActivityThrottleMark = 0;

  // Attach listeners once.
  for (const ev of ACTIVITY_EVENTS) {
    window.addEventListener(ev, _onActivity, { passive: true });
  }

  // Start the refresh timer.
  _refreshIntervalId = setInterval(_tokenRefreshTick, TOKEN_REFRESH_INTERVAL_MS);
}

/**
 * Stop the background service.  Idempotent — calling before start() is a
 * no-op.  Removes listeners and clears the refresh interval.
 */
export function stopAuthBackgroundService() {
  if (!_started) return;
  _started = false;

  for (const ev of ACTIVITY_EVENTS) {
    window.removeEventListener(ev, _onActivity);
  }

  if (_refreshIntervalId != null) {
    clearInterval(_refreshIntervalId);
    _refreshIntervalId = null;
  }
  _onTokenRefreshFail = null;
}

/**
 * Read the last-activity timestamp.  Used by useInactivityTimeout and
 * any other UI that wants to display "You've been idle for X minutes."
 *
 * @returns {number} epoch-ms of the last user interaction.
 */
export function getLastActivityTimestamp() {
  return _lastActivity;
}

/**
 * Test-only: force-reset the service.  Useful in jest/playwright runs
 * that mount and unmount AuthProvider repeatedly.  Not used in prod
 * code paths.
 */
export function _resetAuthBackgroundServiceForTests() {
  stopAuthBackgroundService();
  _lastActivity = Date.now();
  _lastActivityThrottleMark = 0;
}
